import React, { useEffect, useState, useMemo } from "react";
import { validationHelper } from "controls/validationHelper";
import { Button, FormFieldType, Row, SaladToggleButton, useModelStateContext } from "saladsoft-primitive";
import styled from "styled-components";

type SaladFormProps<T> = {
    fields?: FormFieldType<T>[];
    values?: T;
    okText?: string;
    cancelText?: string;
    useButton?: boolean;
    style?: React.CSSProperties;
    inputStyle?: React.CSSProperties;
    onSave?: (model?: T) => void;
    onCancel?: () => void;
    onChange?: (model?: T) => void;
};

type GroupedFields<T> = Record<
    string | number,
    Record<string, FormFieldType<T>[]>
> & {
    withoutGroup: FormFieldType<T>[];
};

const SaladForm = <T,>({
    values,
    fields,
    inputStyle,
    style,
    onSave,
    onCancel,
    onChange,
    okText,
    cancelText,
    useButton = true,
}: SaladFormProps<T>) => {
    const {
        stateModel,
        setState,
        setMultipleState,
        updateServiceModel,
        unmountModel,
        updateServiceModelMultiple,
        serviceModel,
    } = useModelStateContext<T>();

    // const resourceStore = new ResourceStore();
    const [isValid, setisValid] = useState<boolean>(false);
    const [isChanged, setIsChanged] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);

    const initializeWithValues = () => {
        if (isInitialized) return;
        const updates: Partial<T> = {};
        fields?.forEach((field) => {
            if (values && values[field.key] !== undefined) {
                updates[field.key] = values[field.key] as T[keyof T];
            }
        });
        if (Object.keys(updates).length > 0) {
            setMultipleState(updates);
            setIsInitialized(true);
            // updateServiceModelMultiple(updates);
        }
    };

    const initializeWithDefaults = () => {
        const updates: Partial<T> = {};
        fields?.forEach((field) => {
            if (field.defaultValue !== undefined) {
                updates[field.key] = field.defaultValue;
            }
        });
        if (Object.keys(updates).length > 0) {
            setMultipleState(updates);
        }
    };

    const validation = () => {
        let isValid;
        fields
            ?.filter((field) => field.validation)
            .forEach((field) => {
                const value = stateModel?.[field.key];
                switch (field.validation) {
                    case "required":
                        isValid = validationHelper.required(value as string);
                        break;
                    default:
                        isValid = true;
                }
            });

        setisValid(isValid ?? false);
    };

    const handleSave = async () => {
        // if (!fields || !values) return; // fields와 values가 존재하지 않으면 함수 종료

        const silence = fields?.filter((field) => field.silentUpdate);
        const files = fields?.filter((field) => field.controlType === "file");
        const updates: Partial<T> = {};

        // Silence 업데이트 처리
        silence?.forEach((field) => {
            const key = field.key;
            const value = values?.[key];
            if (value !== undefined) {
                updates[key] = value;
            }
        });

        // 파일 업로드 처리
        if (files && files?.length > 0) {
            const uploadPromises = files.map(async (field) => {
                const file = serviceModel.current?.[field.key];
                const key = field.key;

                if (file !== undefined && file instanceof File) {
                    try {
                        // 파일 업로드를 기다린 후 결과를 업데이트에 저장
                        // const response = await resourceStore.uploadFile(file);
                        // updates[key] = response as unknown as T[keyof T]; // 적절한 타입 캐스팅 필요
                    } catch (error) {
                        console.error("File upload failed:", error);
                    }
                }
            });

            // 모든 파일 업로드가 완료될 때까지 기다림
            await Promise.all(uploadPromises);
        }
        const t = updates;
        // 업데이트를 병합하여 onSave 콜백 호출
        const merged = { ...serviceModel.current, ...updates };
        onSave?.(merged as T);
    };

    useEffect(() => {

        initializeWithDefaults(); // values가 없을 때
    }, [fields]);

    useEffect(() => {
        // value는 기본값임.
        initializeWithValues(); // values가 있을 때
    }, [values]);

    useEffect(() => {
        if (!stateModel || Object.keys(stateModel).length === 0) return;

        if (!isLoading) {
            validation();
            onChange?.(stateModel);
        }
    }, [stateModel]);

    useEffect(() => {
        return () => {
            unmountModel();
        };
    }, []);

    useEffect(() => {
        if (fields) {  // fields가 존재할 때만 타이머 설정
            const timer = setTimeout(() => {
                setIsLoading(false);
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [fields]); // fields만 의존성으로 유지

    const getFieldByType = (field: FormFieldType<T>) => {
        if (field.silentUpdate) return <></>;

        const controlType = field.controlType
            ? field.controlType
            : typeof field.value === "string"
                ? "text"
                : typeof field.value === "number"
                    ? "number"
                    : typeof field.value === "boolean"
                        ? "checkbox"
                        : "custom";

        switch (controlType) {
            case "text":
            case "number":
                return (
                    <>


                        <LabelWrapper className="test-class" >
                            {field.label ? field.label : ""}
                            <RequiredLabel>
                                {field.validation === "required" ? "*" : ""}
                            </RequiredLabel>
                        </LabelWrapper>
                        <InputWrapper
                            style={inputStyle}
                            type={controlType}
                            // label={field?.label || ''}
                            value={stateModel?.[field.key] as any}
                            onChange={(e) => {
                                setState(field.key, e.target.value as T[keyof T]);
                            }}
                        />
                    </>


                );
            case "textarea":
                return (
                    <InputWrapper
                        style={inputStyle}
                        type="textarea"
                        value={stateModel?.[field.key] as any}
                        // sx={{ '& .MuiOutlinedInput-root': { paddingLeft: 20 } }}
                        onChange={(e) => {
                            setState(field.key, e.target.value as T[keyof T]);
                        }}
                    />
                );
            case "file":
                return (
                    <Button>
                        {field.label}
                        <VisuallyHiddenInput
                            type="file"
                            onChange={(e) => {
                                setState(field.key, e.target.files?.[0] as T[keyof T]);
                            }}
                        />
                    </Button>
                );
            case "custom":
                return (
                    <>
                        <LabelWrapper className="test-class" >
                            {field.label ? field.label : ""}
                            <RequiredLabel>
                                {field.validation === "required" ? "*" : ""}
                            </RequiredLabel>
                        </LabelWrapper>
                        {field.template && React.cloneElement(field.template, {
                            value: stateModel?.[field.key] as any,
                            onChange: (value: any) => {
                                if (field.template?.props?.onChange) {
                                    field.template.props.onChange(value);
                                }
                                setState(field.key, value);
                            }
                        })}
                    </>
                );
            case "select":
                return (
                    <>
                        <LabelWrapper className="test-class" >
                            {field.label ? field.label : ""}
                            <RequiredLabel>
                                {field.validation === "required" ? "*" : ""}
                            </RequiredLabel>
                        </LabelWrapper>
                        <SaladToggleButton defaultIndex={field.defaultIndex} items={field.options ?? []} onChange={(value: string, isDefault: boolean) => {
                            {
                                setIsChanged(true);
                                if (isDefault) {
                                    updateServiceModel(field.key, value as T[keyof T]);
                                } else {
                                    setState(field.key, value as T[keyof T]);
                                }
                            }
                        }} />
                    </>
                );
            case "richText":
                return (
                    <>RichText</>
                    // <WebEditorWrapper
                    //   value={stateModel?.[field.key] as string}
                    //   onEditorChange={(value?: string) => {
                    //     setState(field.key, value as T[keyof T]);
                    //   }}
                    // />
                );
            case "number":
                return <input value={field.value as any} />;
            case "checkbox":
                const value =
                    typeof serviceModel.current?.[field.key] === "boolean"
                        ? (serviceModel.current[field.key] as boolean)
                        : undefined;
                return (
                    <div style={{ width: "100%" }}>
                        <input
                            type="checkbox"
                            // onChange={(e: any, value: boolean) => {
                            //   setState(field.key, value as T[keyof T]);
                            // }}
                            checked={value || false}
                        />
                    </div>
                );
            default:
                return <></>;
        }
    };

    const organizeFields = (fields: FormFieldType<T>[]): GroupedFields<T> => {
        // 1. groupSeq로 먼저 그룹화하고, 그 안에서 groupId로 다시 그룹화
        const groupedBySeq = fields.reduce((acc, field) => {
            if (field.group?.id) {
                const groupSeq = field.group.groupSeq ?? 0;
                const groupId = field.group.id;

                if (!acc[groupSeq]) {
                    acc[groupSeq] = {};
                }
                if (!acc[groupSeq][groupId]) {
                    acc[groupSeq][groupId] = [];
                }
                acc[groupSeq][groupId].push(field);
            } else {
                if (!acc.withoutGroup) {
                    acc.withoutGroup = [];
                }
                acc.withoutGroup.push(field);
            }
            return acc;
        }, {} as GroupedFields<T>);

        // 각 그룹 내에서 seq로 sala
        Object.values(groupedBySeq).forEach((seqGroup) => {
            if (seqGroup !== groupedBySeq.withoutGroup) {
                Object.values(seqGroup).forEach((group) => {
                    group.sort(
                        (a: any, b: any) => (a.group?.seq ?? 0) - (b.group?.seq ?? 0)
                    );
                });
            }
        });

        return groupedBySeq;
    };

    const organizedFields = useMemo(() =>
        Object.entries(organizeFields(fields || [])),
        [fields]
    );

    return (
        <div style={{ padding: 20, ...style }} className="salad-form">
            <Row
                flexDirection="column"
                width={"100%"}
                gap="20px"
                boxSizing="border-box"
                justifyContent="flex-start"
            >
                <>
                    {/* 그룹이 있는 필드들 - 가로 배치 */}
                    {!isLoading && fields && organizedFields.map(([groupSeq, seqGroups]) => {
                        if (groupSeq === "withoutGroup") {
                            return Array.isArray(seqGroups)
                                ? seqGroups.map(
                                    (field: FormFieldType<T>, index: number) => (
                                        <Row
                                            key={`single-${index}`}
                                            width="100%"
                                            flexDirection="column"
                                        >
                                            <>
                                                {(serviceModel.current?.[field.dependsOn as keyof T] === field.dependsOnValue) && (
                                                    <>
                                                        <FieldItem style={{ maxWidth: "100%" }}>
                                                            <FieldItemWrapper>
                                                                {getFieldByType(field) || <></>}
                                                            </FieldItemWrapper>
                                                        </FieldItem>
                                                    </>
                                                )}
                                            </>
                                        </Row>
                                    )
                                )
                                : null;
                        }

                        return (
                            <GridContainer key={groupSeq}>
                                <Row
                                    width="100%"
                                    flexDirection="row"
                                    gap="10px"
                                    flexWrap="wrap"
                                >
                                    {Object.entries(seqGroups).map(([groupId, fields]) =>
                                        fields.map((field: any, index: number) => (
                                            <>
                                                {(serviceModel.current?.[field.dependsOn as keyof T] === field.dependsOnValue) && (
                                                    <>
                                                        <FieldItem key={`${groupId}-${index}`}>
                                                            <FieldItemWrapper>
                                                                {getFieldByType(field) || <></>}
                                                            </FieldItemWrapper>
                                                        </FieldItem>
                                                    </>
                                                )}
                                            </>
                                        ))
                                    )}
                                </Row>
                            </GridContainer>
                        );
                    })}
                    {isLoading && <div>Loading...</div>}
                </>
            </Row>
            {useButton && (
                <Row justifyContent="flex-end" marginTop="50px" gap="10px">
                    <Button
                        type="outlined"
                        onClick={() => {
                            onCancel?.();
                        }}
                    >
                        {cancelText || "Cancel"}
                    </Button>
                    <Button
                        isValid={isValid}
                        onClick={() => {
                            handleSave();
                        }}
                    >
                        {okText || "Save"}
                    </Button>
                </Row>
            )}
        </div>
    );
};

const VisuallyHiddenInput = styled.input`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1;
`;

const FieldItem = styled.div`
  flex: 1;
  width: 100%;
`;

const FieldItemWrapper = styled.div`
  width: 100%;
`;

const InputWrapper = styled.input`
  width: 100%;
  padding: 10px;
  min-height: 40px;
  box-sizing: border-box;
  border-radius: 5px;
  border: 1px solid #ccc;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: #007bff;
    outline: none;
    transition: border-color 0.3s ease;
  }
`;

const RequiredLabel = styled.span`
  color: red;
`;

const LabelWrapper = styled.div`
  font-size: 0.9rem;
  margin-bottom: 5px;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  grid-gap: 10px;
  width: 100%;
`;

export default SaladForm;
